// React
import { useState, useReducer, useEffect, useContext } from "react";

// context
import ProductContext, { ProductProvider } from "../context/ProductContext";
import { initProductData, productReducer } from "../context/productReducer";

// api
import getProduct from "../api/product";
import getUser from "../api/user";

// Components
import LoadingSkeleton from "./loadingSkeleton/loadingSkeleton.component";
import Container from "./container/Container";
import InventorySearchForm from "./InventorySearchForm";
import InventoryNoResults from "./InventoryNoResults";
import InventoryResultsPricingAndAvailibility from "./InventoryResultsPricingAndAvailibility";
import SignIn from "./SignIn/SignIn";

const Inventory = () => {
  const context: any = useContext(ProductContext);

  const {
    productDispatch,
    productState: {
      IsUserLoggedIn,
      InventoryStatusCode,
      InventoryStatusMessage,
      isProductDiscontinued,
      ProductNumber,
      ProductStatus,
      Quantity,
      isSubmitted,
      ReplacementProductText,
      ReplacementProductUrl,
    },
  }: {
    productDispatch: any;
    productState: {
      IsUserLoggedIn: boolean;
      InventoryStatusCode: number;
      InventoryStatusMessage: string;
      isProductDiscontinued: boolean;
      ProductNumber: string;
      ProductStatus: any[];
      Quantity: string;
      isSubmitted: boolean;
      ReplacementProductText: string | null;
      ReplacementProductUrl: string | null;
    };
  } = context;

  const [localProductNumber, setLocalProductNumber] = useState("");

  const [isLoading, setLoading] = useState(false);

  const scrollToResultsMobile = () => {
    const screenWidth: number = window.screen.width;
    const notMobileView = 481;
    if (ProductNumber && screenWidth < notMobileView) {
      const heroPxlHeight: number =
        document.getElementsByClassName("_hero")[0].clientHeight;
      const boxPxlHeight: number =
        document.getElementsByClassName("box")[0].clientHeight;
      const overlapDiff: number = boxPxlHeight - heroPxlHeight;
      const finalOffSet: number = boxPxlHeight + overlapDiff;
      window.parent.scrollTo({
        top: finalOffSet,
        behavior: "smooth",
      });
    }
  };

  const setQuery = (skuItemName: string, qty: string) => {
    const qtyAsInt: number = parseInt(qty);
    if (window.history.pushState) {
      let newUrl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname;

      if (skuItemName) {
        newUrl += "?sku=" + skuItemName;

        if (qtyAsInt > 1) newUrl += "&qty=" + qtyAsInt;
      }

      window.history.pushState({ path: newUrl }, "", newUrl);
    }
  };

  // skuParameterUrl and qtyParameterUrl are only used on the inital load if there are no url parameters
  // this is where the logic for the url to maintain state on redirect and refresh
  const getProductHandler = (
    skuParameterUrl: any = null,
    qtyParameterUrl: any = null,
  ) => {
    setLoading(true);
    setTimeout(() => {
      scrollToResultsMobile();
    }, 100);

    // these variables are used to determine if we are querying the product based on url or state from user
    const nonUrlProductSku = localProductNumber ?? "";
    const productSku =
      null !== skuParameterUrl ? skuParameterUrl : nonUrlProductSku;
    const nonUrlQty = Quantity ?? "1";
    const productQty = qtyParameterUrl ? qtyParameterUrl : nonUrlQty;

    getProduct({
      skuItemName: productSku,
      qty: productQty,
    }).then((data) => {
      setLoading(false);

      productDispatch({
        type: "SET_PRODUCT",
        payload: data,
      });

      data.InventoryStatusCode === 0 && removeQuery();
      if (data && data.InventoryStatusCode !== 2) {
        setQuery(data.ProductNumber, data.Quantity);
      } else {
        removeQuery();
      }
    });
  };

  const removeQuery = () => {
    const url = window.location.href;
    return url.split(/[?#]/)[0];
  };

  const ifQueryStringUpdateState = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sku = urlParams.get("sku");
    const qty = urlParams.get("qty");
    // set qty in state
    if (qty) {
      const qtyAsString: string = JSON.stringify(qty);
      productDispatch({
        type: "SET_COUNTER_QUANTITY",
        payload: qtyAsString,
      });
    }
    // set product in state then submit
    if (sku) {
      productDispatch({
        type: "SET_PRODUCT_NUMBER",
        payload: sku,
      });
      const optionalQty = qty ? qty : "1";
      getProductHandler(sku, optionalQty);
    }
  };

  // stops skeleton showing on initial load,
  // empty array causes useEffect to only fire on load once
  useEffect(() => {
    setLoading(false);
    // eslint-disable-next-line
    ifQueryStringUpdateState();
  }, []);

  useEffect(() => {
    if (isSubmitted) {
      getProductHandler();

      productDispatch({
        type: "SET_SUBMITTED",
        payload: false,
      });
    }
  }, [isSubmitted]);

  async function getCurrentUser() {
    const result = await getUser();
    productDispatch({
      type: "SET_USER",
      payload: result,
    });
    if (result.User !== null) {
      productDispatch({
        type: "SET_SUBMITTED",
        payload: true,
      });
    }
  }

  useEffect(() => {
    if (IsUserLoggedIn === false) {
      getCurrentUser();
    }
  }, [IsUserLoggedIn]);

  return (
    <Container>
      {IsUserLoggedIn ? (
        <InventorySearchForm productNumberParent={setLocalProductNumber} />
      ) : (
        <>{!isLoading && <SignIn />}</>
      )}
      {IsUserLoggedIn && (
        <>
          {isLoading ? (
            <LoadingSkeleton />
          ) : InventoryStatusCode === 2 || InventoryStatusCode === 6 ? (
            <InventoryNoResults
              InventoryStatusMessage={InventoryStatusMessage}
              InventoryStatusCode={InventoryStatusCode}
            />
          ) : (
            <InventoryResultsPricingAndAvailibility
              ProductNumber={ProductNumber}
              ProductStatus={ProductStatus}
              isProductDiscontinued={isProductDiscontinued}
              ReplacementProductText={ReplacementProductText}
              ReplacementProductUrl={ReplacementProductUrl}
              InventoryStatusMessage={InventoryStatusMessage}
            />
          )}
        </>
      )}
    </Container>
  );
};

const InventoryProvider = () => {
  const [productState, productDispatch]: [
    initialState: any,
    initialDispatch: any,
  ] = useReducer(productReducer, initProductData);

  return (
    <ProductProvider value={{ productState, productDispatch }}>
      <Inventory />
    </ProductProvider>
  );
};

export default InventoryProvider;
